import React from "react";
import { Modal, Stack, Text, PrimaryButton, DefaultButton } from "@fluentui/react";
import { Utils } from "../Utils";
import { useId } from "@fluentui/react-hooks";

/**
 * The props interface for the confirmation dialog
 */
export interface IConfirmDialogProps extends IConfirmDialogStateProps, IConfirmDialogDispatch {}

/**
 * The confirmation dialog state props
 */
interface IConfirmDialogStateProps {
  /**
   * The message to display in the confirmation
   */
  message: string;

  /**
   * A value indicating whether the dialog is visible
   */
  visible: boolean;
}

/**
 * The dispatch props
 */
interface IConfirmDialogDispatch {
  /**
   * When a user elects to proceed with the change
   */
  onContinue?: () => void;

  /**
   * When a user elects to cancel the change
   */
  onCancel?: () => void;
}

/**
 * The Confirmation Dialog component
 *
 * @param props - the confirm dialog component props
 * @returns the markup
 */
export const ConfirmDialog: React.FC<IConfirmDialogProps> = ({ message, visible, onContinue, onCancel }) => {
  const dialogTitleId = useId();

  return (
    <Modal
      isBlocking
      isOpen={visible}
      titleAriaId={dialogTitleId}
      styles={{
        main: {
          minHeight: 172,
          minWidth: 288,
          maxWidth: 600,
          selectors: {
            [Utils.mobileQuery]: {
              minHeight: "30vh",
              minWidth: "unset",
            },
          },
        },
      }}
    >
      <Stack styles={{ root: { padding: 15 } }}>
        <Stack grow={0} styles={{ root: { marginBottom: 15 } }}>
          <Text variant="large" id={dialogTitleId}>
            {ClientStrings.ConfirmDialogTitle}
          </Text>
        </Stack>
        <Stack grow={2}>
          <Text>{message}</Text>
        </Stack>
        <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 8, padding: 10 }} grow={0}>
          <PrimaryButton
            onClick={(): void => {
              if (onContinue) {
                onContinue();
              }
            }}
            styles={{ root: { borderWidth: 1, borderStyle: "solid" } }}
          >
            {ClientStrings.ContinueButtonText}
          </PrimaryButton>
          <DefaultButton
            onClick={(): void => {
              if (onCancel) {
                onCancel();
              }
            }}
          >
            {ClientStrings.CancelButtonText}
          </DefaultButton>
        </Stack>
      </Stack>
    </Modal>
  );
};
